import { CallToolResult } from '@modelcontextprotocol/sdk/types.js';
import { Ok } from 'ts-results-es';
import { z } from 'zod';

import {
  CustomViewNotAllowedError,
  FeatureDisabledError,
  UnknownError,
} from '../../../errors/mcpToolError.js';
import { useRestApi } from '../../../restApiInstance.js';
import { ProductVersion } from '../../../sdks/tableau/types/serverInfo.js';
import { WebMcpServer } from '../../../server.web.js';
import { resourceAccessChecker } from '../resourceAccessChecker.js';
import { WebTool } from '../tool.js';
import { getImageFormatForVersion } from './getImageFormatForVersion.js';
import {
  buildImageToolResult,
  ImageToolResult,
  imageToolResultToCallToolResult,
} from './imageToolResult.js';

const paramsSchema = {
  customViewId: z.string(),
  width: z.number().gt(0).int().optional(),
  height: z.number().gt(0).int().optional(),
  format: z
    .enum(['PNG', 'SVG'])
    .optional()
    .describe(
      'The image format to return. Use "PNG" (default) when the image will be analyzed or interpreted. Use "SVG" when the image will be displayed to the user — SVG is scalable and produces smaller file sizes.',
    ),
  viewFilters: z
    .record(z.string())
    .optional()
    .describe('Optional map of view filter field names to values.'),
};

export type GetCustomViewImageError = {
  type: 'custom-view-not-allowed';
  message: string;
};

export const getGetCustomViewImageTool = (
  server: WebMcpServer,
  tableauServerVersion: ProductVersion,
): WebTool<typeof paramsSchema> => {
  const getCustomViewImageTool = new WebTool({
    server,
    name: 'get-custom-view-image',
    description: [
      'Retrieves an image of the specified custom view in a published viz.',
      'A custom view is a shortcut to a specific state of interaction, such as filter selections and sorting, for a published viz.',
      'Requires the custom view LUID from the content URL (not the published view id).',
      'Optional width and height in pixels control render size.',
      'Optional view field names and values can be provided to filter the custom view.',
      'For published views, use the tool to get view image by view id instead.',
    ].join(' '),
    paramsSchema,
    annotations: {
      title: 'Get Custom View Image',
      readOnlyHint: true,
      destructiveHint: false,
      idempotentHint: true,
      openWorldHint: false,
    },
    callback: async (
      { customViewId, width, height, format, viewFilters },
      extra,
    ): Promise<CallToolResult> => {
      return await getCustomViewImageTool.logAndExecute<ImageToolResult>({
        extra,
        args: { customViewId, width, height, format, viewFilters },
        callback: async () => {
          const formatResult = getImageFormatForVersion(format, tableauServerVersion);
          if (formatResult.isErr()) {
            return formatResult;
          }

          const isAllowedResult = await resourceAccessChecker.isCustomViewAllowed({
            customViewId,
            extra,
          });

          if (!isAllowedResult.allowed) {
            return new CustomViewNotAllowedError(isAllowedResult.message).toErr();
          }

          const imageResult = await useRestApi({
            ...extra,
            jwtScopes: getCustomViewImageTool.requiredApiScopes,
            callback: async (restApi) => {
              return await restApi.viewsMethods.getCustomViewImage({
                customViewId,
                siteId: restApi.siteId,
                width,
                height,
                resolution: 'high',
                format: formatResult.value,
                viewFilters,
              });
            },
          });

          if (imageResult.isErr()) {
            if (imageResult.error.type === 'feature-disabled') {
              return new FeatureDisabledError(
                'The image format feature is disabled on this Tableau Server.',
              ).toErr();
            }
            return new UnknownError(imageResult.error.message, 400).toErr();
          }

          // Offload to S3 (returning a presigned URL) when configured, otherwise
          // carry the raw bytes for inline base64. Falls back to inline on any
          // S3 failure.
          return new Ok(
            await buildImageToolResult({
              imageData: imageResult.value,
              format,
              resourceId: customViewId,
              config: extra.config,
              toolName: getCustomViewImageTool.name,
              keyPrefixSegment: 'custom-view-images/',
            }),
          );
        },
        constrainSuccessResult: (imageToolResult) => {
          return {
            type: 'success',
            result: imageToolResult,
          };
        },
        getSuccessResult: (imageToolResult) => imageToolResultToCallToolResult(imageToolResult),
      });
    },
  });

  return getCustomViewImageTool;
};
